import React, { ReactNode, createContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAccessTokenFromResfreshToken } from 'utils/getAccessToken';
import verifyAwsToken from './verifyToken';
import { useUserDetails } from 'apis/User/getUserDetails';

interface AuthContextType {
    userId: string | null
    username: string | null
    isStaff: boolean
    isUserLogged: boolean
}

const AuthContext = createContext<AuthContextType>({
    userId: null,
    username: null,
    isStaff: false,
    isUserLogged: false
});

interface AuthProviderProps {
    children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const LOGIN_URL = `${process.env.REACT_APP_LOGIN_URL}/login`
    const [searchParams, setSearchParams] = useSearchParams();
    const {data: userDetails, isLoading: isLoadingUserDetails} = useUserDetails({config: {cacheTime: 1}});
    const [userId, setUserId] = useState('');
    const [isStaff, setIsStaff] = useState(false);
    const [isUserLogged, setIsUserLogged] = useState(false);

    let access_token = searchParams.get('access_token') || localStorage.getItem('access_token');
    let refresh_token = searchParams.get('refresh_token') || localStorage.getItem('refresh_token');
    let username = searchParams.get('username') || localStorage.getItem('username');
    let urlContainsToken = !!searchParams.get('access_token') || !!searchParams.get('refresh_token') || !!searchParams.get('username')


    useEffect(() => {
        if(userDetails){
            setIsStaff(userDetails.is_staff)
        }
    }, [userDetails])

    useEffect(() => {
        const initializeAuth = async () => {
            if (!access_token || !refresh_token || !username) {
                window.location.href = LOGIN_URL;
                return;
            }

            try {
                const { status, payload } = await verifyAwsToken(access_token);
                if (status && payload) {
                    //Set User credentials
                    setUserId(payload.username);
                    setIsUserLogged(true)

                    //Set Items to Local Storage
                    localStorage.setItem('access_token', access_token!);
                    localStorage.setItem('refresh_token', refresh_token!);
                    localStorage.setItem('username', username!);
                } else {
                    if (refresh_token) {
                        try {
                            const newAccessToken = await getAccessTokenFromResfreshToken(refresh_token);
                            localStorage.setItem('access_token', newAccessToken);
                            localStorage.setItem('refresh_token', refresh_token!);
                            localStorage.setItem('username', username!);
                            setIsUserLogged(true)
                        } catch (error) {
                            console.error('Error refreshing token:', error);
                            window.location.href = LOGIN_URL;
                        }
                    } else {
                        window.location.href = LOGIN_URL;
                    }
                }

                //Remove Query params after setting in local storage
                if(urlContainsToken){
                    searchParams.delete('access_token');
                    searchParams.delete('refresh_token');
                    searchParams.delete('username');
                    setSearchParams(searchParams)
                }
            } catch (error) {
                console.error('Error verifying token:', error);
                window.location.href = LOGIN_URL;
            }
        };

        initializeAuth();
    }, []);

    return (
        <AuthContext.Provider value={{ userId, username, isUserLogged, isStaff }}>
            {children}
        </AuthContext.Provider>
    );
};

export { AuthProvider };
export default AuthContext;